import { useCallback, useEffect, useState } from "preact/hooks";

import {
  dismissPrompt,
  getVisitCount,
  hasCompletedOnce,
  isPromptDismissed,
  recordCompletionOnce,
  recordVisit,
  shouldShowOptInPrompt,
} from "../lib/reminders";
import type { ReminderSupport } from "./useReminderPrefs";

export interface UseReminderOptInResult {
  /** Whether the Today gentle-prompt-card should render right now. */
  showPrompt: boolean;
  /** "Yes please" — subscribes this device via the shared `enable()` and
   * hides the card either way (a granted-vs-denied outcome is then the
   * More tab's honest-state row to explain, never a second ambush). */
  accept: () => void;
  /** "Not now" (or dismissing) — remembers the choice, never nags again;
   * still re-offerable any time from the More tab's toggle. */
  decline: () => void;
  /** Call once, right after a task is marked done (not undone) — the
   * "first meaningful use" half of the opt-in heuristic. */
  recordCompletion: () => void;
}

/**
 * Sprint-005 C6 — gates the Today opt-in card
 * (`components/ReminderOptInCard.tsx`) on a simple, FLAGGED heuristic
 * (task brief: "after first completion or second visit — pick simple
 * heuristic, flag it"): shown once this device has either completed at
 * least one task, ever, OR opened the app at least twice — never on the
 * very first tap of a brand-new visit, and never again once answered
 * (accept/decline) or once this device is already subscribed some other
 * way (e.g. re-enabled from the More tab directly).
 *
 * **E2E-gating note (flagged for QA):** this heuristic is intentionally
 * name-spaced under `localStorage` keys the e2e suite's shared session
 * fixture pre-seeds as "already dismissed" (see
 * `e2e/global-setup.ts::mintE2eSession`) — every existing spec inherits
 * `promptDismissed=true` and never sees this card at all, regardless of
 * how many tasks a given spec completes or how many times it reloads.
 * The dedicated reminders e2e spec exercises the real subscribe/
 * unsubscribe round trip through the More tab's always-available toggle
 * instead of this auto-prompt, which sidesteps needing to fight (or
 * rely on the precise shape of) this heuristic in a scripted browser
 * context entirely.
 */
export function useReminderOptIn(support: ReminderSupport): UseReminderOptInResult {
  const [dismissed, setDismissed] = useState(isPromptDismissed);
  const [visitCount, setVisitCount] = useState(getVisitCount);
  const [completedOnce, setCompletedOnce] = useState(hasCompletedOnce);

  useEffect(() => {
    setVisitCount(recordVisit());
  }, []);

  const recordCompletion = useCallback(() => {
    if (completedOnce) return;
    recordCompletionOnce();
    setCompletedOnce(true);
  }, [completedOnce]);

  const decline = useCallback(() => {
    dismissPrompt();
    setDismissed(true);
  }, []);

  const accept = useCallback(() => {
    dismissPrompt();
    setDismissed(true);
  }, []);

  const showPrompt =
    support !== "checking" &&
    shouldShowOptInPrompt({
      alreadyDismissed: dismissed,
      alreadySubscribed: support === "subscribed",
      // An iPhone/iPad that still has to be added to the Home Screen (or
      // updated) gets the card too — with the steps instead of "Yes please"
      // — since that's exactly the person who'd otherwise never hear about
      // reminders. Denied stays the More tab's job (they already said no).
      canPrompt:
        support === "unsubscribed" || support === "needs_install" || support === "needs_update",
      visitCount,
      completedOnce,
    });

  return { showPrompt, accept, decline, recordCompletion };
}
